<script setup>
/**
 * AgentClientsView
 * Mobile-first clients list filtered by agent number
 */
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '../../../app/store/index.js'
import { subscribeClientsByAgent, subscribeClients } from '../../../core/services/clients.js'
import { ROUTES } from '../../../core/config/constants.js'
import { usePagination } from '@core/composables/usePagination.js'
import { useFirestoreSubscription } from '@core/composables/useFirestoreSubscription.js'
import { loadTodayRoute } from '@core/services/agentRoutes.js'

// ============================================
// Router
// ============================================
const router = useRouter()

// ============================================
// State
// ============================================
const authStore = useAuthStore()

const clients = ref([])
const isLoading = ref(true)
const error = ref(null)

// Tab toggle (agents only): 'clients' = all clients, 'route' = today's route
// Default to 'route' for agents — will auto-switch to 'clients' if route is empty
const activeTab = ref('route')
const routeMakats = ref(null) // Set of makats from saved route, null = not loaded yet
const isLoadingRoute = ref(false)
const routeLoaded = ref(false) // whether we attempted to load the route

// Filters
const searchQuery = ref('')
const cityFilter = ref('')

// Debounce timer for search
let searchDebounce = null

const { track, unsubscribeAll } = useFirestoreSubscription()

// ============================================
// Computed: Role checks
// ============================================
const { isAdmin } = storeToRefs(authStore)

// ============================================
// Computed: Agent number from user profile
// ============================================
const userAgentNumber = computed(() => {
  const agentValue = authStore.user?.profile?.agent
  if (agentValue === null || agentValue === undefined || agentValue === '') {
    return null
  }
  const num = Number(agentValue)
  return isNaN(num) ? null : num
})

// Admin can view without agent number, agents need valid agent number
const canViewClients = computed(() => isAdmin.value || userAgentNumber.value !== null)

// ============================================
// Computed: Unique cities for filter dropdown
// ============================================
const uniqueCities = computed(() => {
  const cities = new Set()
  clients.value.forEach((c) => {
    if (c.city) cities.add(c.city)
  })
  return Array.from(cities).sort()
})

// ============================================
// Route tab: load saved route
// ============================================
async function loadRoute() {
  const uid = authStore.userId
  if (!uid) return

  isLoadingRoute.value = true
  try {
    const routeClients = await loadTodayRoute(uid)
    if (routeClients.length > 0) {
      routeMakats.value = new Set(routeClients.map((c) => c.makat))
    } else {
      routeMakats.value = new Set() // empty = no route saved
      // Auto-switch to all clients if route is empty
      if (activeTab.value === 'route') activeTab.value = 'clients'
    }
  } catch (_err) {
    routeMakats.value = new Set()
    if (activeTab.value === 'route') activeTab.value = 'clients'
  } finally {
    isLoadingRoute.value = false
    routeLoaded.value = true
  }
}

function switchTab(tab) {
  activeTab.value = tab
  resetPage()
  if (tab === 'route' && !routeLoaded.value) {
    loadRoute()
  }
}

// Whether the route tab shows an empty state
const routeIsEmpty = computed(
  () => activeTab.value === 'route' && routeLoaded.value && routeMakats.value?.size === 0
)

// ============================================
// Computed: Filtered clients (client-side)
// ============================================
const filteredClients = computed(() => {
  let result = clients.value

  // Route filter — only show clients in today's saved route
  if (activeTab.value === 'route' && routeMakats.value && routeMakats.value.size > 0) {
    result = result.filter((c) => routeMakats.value.has(c.makat))
  }

  // Search filter (makat, name, vat)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.trim().toLowerCase()
    result = result.filter((client) => {
      const makat = (client.makat || '').toLowerCase()
      const name = (client.name || '').toLowerCase()
      const vat = (client.vat || '').toLowerCase()
      return makat.includes(query) || name.includes(query) || vat.includes(query)
    })
  }

  // City filter
  if (cityFilter.value) {
    result = result.filter((c) => c.city === cityFilter.value)
  }

  return result
})

// ============================================
// Pagination
// ============================================
const {
  currentPage,
  pageSize,
  totalPages,
  paginatedItems: paginatedClients,
  paginationInfo,
  pageSizeOptions,
  resetPagination: resetPage,
  changePageSize,
  prevPage,
  nextPage,
} = usePagination(filteredClients, {
  defaultPageSize: 50,
  pageSizeOptions: [25, 50, 100],
})

// Hide pagination when total clients are fewer than 100
const showPagination = computed(() => filteredClients.value.length >= 100)

// When pagination is hidden, show all filtered clients directly
const displayedClients = computed(() =>
  showPagination.value ? paginatedClients.value : filteredClients.value
)

// ============================================
// Helpers
// ============================================
function formatValue(val) {
  if (val === null || val === undefined || val === '') return '—'
  return val
}

function handleSearchInput() {
  // Debounce search input
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => {
    // Search is already reactive via computed
  }, 200)
}

function clearFilters() {
  searchQuery.value = ''
  cityFilter.value = ''
  currentPage.value = 1
}

// ============================================
// Navigation
// ============================================
function openClientDetails(client) {
  // Pre-unsubscribe Firestore listeners before navigating to avoid
  // SDK connection contention that delays reads on the next page.
  unsubscribeAll()
  router.push({ name: ROUTES.AGENTS_CLIENT_DETAILS, params: { clientId: client.id } })
}

// ============================================
// Subscription management
// ============================================
function setupSubscription() {
  // Clear existing subscription
  unsubscribeAll()

  // Reset state
  clients.value = []
  error.value = null

  // Check if user can view clients
  if (!canViewClients.value) {
    isLoading.value = false
    return
  }

  isLoading.value = true

  // Admin sees ALL clients, agents see only their assigned clients
  if (isAdmin.value) {
    track(
      subscribeClients((data, err) => {
        if (err) {
          console.error('Admin clients subscription error:', err)
          error.value = err.message || 'שגיאה בטעינת לקוחות'
        } else {
          clients.value = data
          error.value = null
        }
        isLoading.value = false
      })
    )
  } else {
    track(
      subscribeClientsByAgent(userAgentNumber.value, (data, err) => {
        if (err) {
          console.error('Agent clients subscription error:', err)
          error.value = err.message || 'שגיאה בטעינת לקוחות'
        } else {
          clients.value = data
          error.value = null
        }
        isLoading.value = false
      })
    )
  }
}

// ============================================
// Lifecycle
// ============================================
onMounted(() => {
  setupSubscription()
  // Eagerly load today's route for agents so we can default to the route tab
  if (!isAdmin.value) loadRoute()
})

onUnmounted(() => {
  if (searchDebounce) clearTimeout(searchDebounce)
})

// Watch for agent number or role changes (e.g., profile update)
watch([userAgentNumber, isAdmin], () => {
  setupSubscription()
})

// Reset page when filters change
watch([searchQuery, cityFilter], () => {
  resetPage()
})
</script>

<template>
  <div class="agent-clients-view">
    <!-- Page Header -->
    <div class="page-header">
      <!-- Admin: plain title -->
      <template v-if="isAdmin">
        <h1 class="page-title">לקוחות</h1>
        <p class="page-subtitle admin-badge">כל הלקוחות (מנהל)</p>
      </template>

      <!-- Agent: tab toggle -->
      <template v-else>
        <div class="header-tabs">
          <button
            class="header-tab"
            :class="{ active: activeTab === 'clients' }"
            @click="switchTab('clients')"
          >
            לקוחות
          </button>
          <button
            class="header-tab"
            :class="{ active: activeTab === 'route' }"
            @click="switchTab('route')"
          >
            מסלול היום
          </button>
        </div>
        <p v-if="userAgentNumber" class="page-subtitle">סוכן {{ userAgentNumber }}</p>
      </template>
    </div>

    <!-- No Agent State (only for non-admin without agent number) -->
    <div v-if="!canViewClients" class="no-agent-state">
      <div class="no-agent-icon">
        <svg
          viewBox="0 0 24 24"
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      </div>
      <p class="no-agent-text">לא מוגדר סוכן למשתמש</p>
      <p class="no-agent-hint">פנה למנהל המערכת לשיוך מספר סוכן</p>
    </div>

    <!-- Main Content (only if valid agent) -->
    <template v-else>
      <!-- Filters Bar -->
      <div class="filters-bar">
        <div class="search-container">
          <svg
            class="search-icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="חיפוש לפי מקט / שם / ח.פ"
            @input="handleSearchInput"
          />
        </div>

        <div class="city-filter">
          <select v-model="cityFilter" class="city-select">
            <option value="">כל הערים</option>
            <option v-for="city in uniqueCities" :key="city" :value="city">{{ city }}</option>
          </select>
        </div>

        <button v-if="searchQuery || cityFilter" class="btn-clear" @click="clearFilters">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          נקה
        </button>
      </div>

      <!-- Results & Pagination Bar -->
      <div class="results-bar">
        <div class="results-info">
          <span class="results-count">
            <template v-if="showPagination">
              {{ paginationInfo.start }}–{{ paginationInfo.end }} מתוך
            </template>
            {{ filteredClients.length }} לקוחות
          </span>
        </div>
        <div v-if="showPagination" class="pagination-controls">
          <div class="page-size-selector">
            <label class="page-size-label">הצג:</label>
            <select
              :value="pageSize"
              class="page-size-select"
              @change="changePageSize(Number($event.target.value))"
            >
              <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
          </div>
          <div class="page-nav">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="prevPage"
              aria-label="עמוד קודם"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
            <button
              class="page-btn"
              :disabled="currentPage === totalPages"
              @click="nextPage"
              aria-label="עמוד הבא"
            >
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Route Loading State -->
      <div v-if="activeTab === 'route' && isLoadingRoute" class="loading-state">
        <div class="loading-skeleton">
          <div class="skeleton-card" v-for="i in 3" :key="i">
            <div class="skeleton-line long"></div>
            <div class="skeleton-line medium"></div>
            <div class="skeleton-line short"></div>
          </div>
        </div>
      </div>

      <!-- Route Empty State -->
      <div v-else-if="routeIsEmpty" class="empty-state">
        <svg
          viewBox="0 0 24 24"
          width="40"
          height="40"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <p class="empty-text">לא נמצא מסלול להיום</p>
        <p class="empty-hint">ניתן לבנות מסלול במסך תכנון מסלול היום</p>
      </div>

      <!-- Loading State -->
      <div v-else-if="isLoading" class="loading-state">
        <div class="loading-skeleton">
          <div class="skeleton-card" v-for="i in 5" :key="i">
            <div class="skeleton-line long"></div>
            <div class="skeleton-line medium"></div>
            <div class="skeleton-line short"></div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <svg
          viewBox="0 0 24 24"
          width="40"
          height="40"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p class="error-text">{{ error }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredClients.length === 0" class="empty-state">
        <svg
          viewBox="0 0 24 24"
          width="40"
          height="40"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
        </svg>
        <p class="empty-text">
          {{ isAdmin ? 'לא נמצאו לקוחות במערכת' : 'לא נמצאו לקוחות לסוכן זה' }}
        </p>
        <p v-if="searchQuery || cityFilter" class="empty-hint">נסה לשנות את החיפוש או הסינון</p>
      </div>

      <!-- Desktop Table -->
      <div v-else class="table-container desktop-only">
        <table class="clients-table">
          <thead>
            <tr>
              <th>מק״ט</th>
              <th>שם</th>
              <th>לקוח מרכז</th>
              <th>עיר</th>
              <th>ח.פ / ת.ז</th>
              <th>טלפון</th>
              <th class="th-chevron"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="client in displayedClients"
              :key="client.id"
              class="clickable-row"
              @click="openClientDetails(client)"
            >
              <td class="cell-makat">{{ formatValue(client.makat) }}</td>
              <td class="cell-name">{{ formatValue(client.name) }}</td>
              <td class="cell-merakez">{{ formatValue(client.merakez) }}</td>
              <td>{{ formatValue(client.city) }}</td>
              <td>{{ formatValue(client.vat) }}</td>
              <td class="cell-phone">{{ formatValue(client.phone) }}</td>
              <td class="cell-chevron">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile Cards -->
      <div
        v-if="!isLoading && !error && filteredClients.length > 0"
        class="cards-container mobile-only"
      >
        <div
          v-for="client in displayedClients"
          :key="client.id"
          class="client-card"
          @click="openClientDetails(client)"
        >
          <!-- Card Header -->
          <div class="card-header">
            <div class="card-title-section">
              <span class="card-name">{{ formatValue(client.name) }}</span>
              <span class="card-makat">#{{ formatValue(client.makat) }}</span>
            </div>
          </div>

          <!-- Card Body - Key Info Grid -->
          <div class="card-info-grid">
            <div class="info-item">
              <svg
                class="info-icon"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span class="info-value">{{ formatValue(client.city) }}</span>
            </div>
            <div class="info-item">
              <svg
                class="info-icon"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
                />
              </svg>
              <span class="info-value info-phone">{{ formatValue(client.phone) }}</span>
            </div>
            <div v-if="client.merakez" class="info-item">
              <svg
                class="info-icon"
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <span class="info-value">מרכז {{ client.merakez }}</span>
            </div>
          </div>

          <!-- Card Footer -->
          <div class="card-footer">
            <span class="card-vat">ח.פ {{ formatValue(client.vat) }}</span>
            <div class="card-action">
              <span class="action-text">פתיחה</span>
              <svg
                class="action-chevron"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* ============================================
   Page Layout
   ============================================ */
.agent-clients-view {
  padding: var(--spacing-md);
  max-width: 1200px;
  margin: 0 auto;
  min-height: calc(100vh - var(--header-height));
  display: flex;
  flex-direction: column;
}

.page-header {
  margin-bottom: var(--spacing-md);
}

.page-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.page-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

.page-subtitle.admin-badge {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
  padding: 2px 10px;
  border-radius: var(--border-radius-full);
  font-weight: var(--font-weight-medium);
}

/* ============================================
   Header Tabs (Agent view)
   ============================================ */
.header-tabs {
  display: flex;
  gap: var(--spacing-lg);
}

.header-tab {
  position: relative;
  background: none;
  border: none;
  padding: 0 0 var(--spacing-xs);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family);
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--transition-fast);
  line-height: 1.3;
}

.header-tab::after {
  content: '';
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  height: 2.5px;
  border-radius: 2px;
  background: transparent;
  transition: background var(--transition-fast);
}

.header-tab.active {
  color: var(--color-primary);
}

.header-tab.active::after {
  background: var(--color-primary);
}

.header-tab:not(.active):hover {
  color: var(--text-secondary);
}

/* ============================================
   No Agent State
   ============================================ */
.no-agent-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-2xl);
}

.no-agent-icon {
  color: var(--text-muted);
  margin-bottom: var(--spacing-md);
}

.no-agent-text {
  font-size: var(--font-size-lg);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs);
}

.no-agent-hint {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: 0;
}

/* ============================================
   Filters Bar
   ============================================ */
.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
}

.search-container {
  flex: 1;
  min-width: 200px;
  position: relative;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  padding-right: 40px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-primary);
  transition: all var(--transition-fast);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.city-filter {
  min-width: 140px;
}

.city-select {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.city-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.btn-clear {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-clear:hover {
  border-color: var(--color-error);
  color: var(--color-error);
}

.btn-clear:active {
  background: rgba(239, 68, 68, 0.1);
  transform: scale(0.97);
}

/* ============================================
   Results & Pagination Bar
   ============================================ */
.results-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
}

.results-info {
  display: flex;
  align-items: center;
}

.results-count {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.page-size-label {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
}

.page-size-select {
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-primary);
  cursor: pointer;
  min-width: 60px;
}

.page-size-select:focus {
  outline: none;
  border-color: var(--color-primary);
}

.page-nav {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.page-btn:hover:not(:disabled) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.page-btn:active:not(:disabled) {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
  transform: scale(0.95);
}

.page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.page-indicator {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  min-width: 50px;
  text-align: center;
}

/* ============================================
   Loading State
   ============================================ */
.loading-state {
  flex: 1;
}

.loading-skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.skeleton-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.skeleton-line {
  height: 16px;
  background: linear-gradient(
    90deg,
    var(--bg-tertiary) 25%,
    var(--border-color) 50%,
    var(--bg-tertiary) 75%
  );
  background-size: 200% 100%;
  border-radius: var(--border-radius-sm);
  animation: shimmer 1.5s infinite;
}

.skeleton-line.long {
  width: 80%;
}
.skeleton-line.medium {
  width: 50%;
}
.skeleton-line.short {
  width: 30%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ============================================
   Error / Empty States
   ============================================ */
.error-state,
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--text-muted);
}

.error-text,
.empty-text {
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  margin: var(--spacing-md) 0 0;
}

.empty-hint {
  font-size: var(--font-size-sm);
  color: var(--text-muted);
  margin: var(--spacing-xs) 0 0;
}

/* ============================================
   Desktop Table
   ============================================ */
.table-container {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow-x: auto; /* Allow horizontal scroll if needed on very narrow screens */
}

.clients-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.clients-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--bg-secondary);
}

.clients-table th {
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: right;
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
  font-size: 11px;
  text-transform: uppercase;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.clients-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
  color: var(--text-secondary);
}

.clients-table tbody tr:hover {
  background: var(--bg-secondary);
}

.clickable-row {
  cursor: pointer;
  transition: background var(--transition-fast);
}

.clickable-row:active {
  background: var(--bg-tertiary);
}

.th-chevron,
.cell-chevron {
  width: 40px;
  text-align: center;
}

.cell-chevron {
  color: var(--text-muted);
  opacity: 0;
  transition: opacity var(--transition-fast);
}

.clickable-row:hover .cell-chevron {
  opacity: 1;
}

.cell-makat {
  font-family: monospace;
  font-size: 12px;
  color: var(--color-primary);
}

.cell-name {
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.cell-merakez {
  font-family: monospace;
  font-size: 12px;
  color: var(--text-secondary);
}

.cell-phone {
  direction: ltr;
  text-align: right;
}

/* ============================================
   Album Button (Desktop)
   ============================================ */

/* ============================================
   Mobile Cards
   ============================================ */
.cards-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: var(--spacing-xl);
}

.client-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 16px;
  padding-right: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  min-height: 120px;
  position: relative;
  border-right: 3px solid var(--color-primary);
}

.client-card:active {
  transform: scale(0.985);
  background: var(--bg-secondary);
  box-shadow: 0 0 0 rgba(0, 0, 0, 0);
  border-right-color: var(--color-primary);
}

/* Card Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.card-title-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0; /* Enable text truncation */
}

.card-name {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-makat {
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  color: var(--color-primary);
  font-weight: 500;
  letter-spacing: 0.02em;
}

.card-header-right {
  flex-shrink: 0;
}

/* Card Info Grid */
.card-info-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-icon {
  color: var(--text-muted);
  flex-shrink: 0;
}

.info-value {
  font-size: 14px;
  color: var(--text-secondary);
}

.info-phone {
  direction: ltr;
  font-variant-numeric: tabular-nums;
}

/* Card Footer */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
  margin-top: auto;
}

.card-vat {
  font-size: 12px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.card-action {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-primary);
  font-size: 13px;
  font-weight: 500;
}

.action-text {
  opacity: 0.9;
}

.action-chevron {
  transition: transform 0.15s ease;
}

.client-card:active .action-chevron {
  transform: translateX(-2px);
}

/* ============================================
   Responsive
   ============================================ */
.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .agent-clients-view {
    padding: 12px;
    padding-bottom: 24px;
  }

  /* Mobile Header */
  .page-header {
    margin-bottom: 16px;
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }

  .page-title {
    font-size: 20px;
    font-weight: 700;
  }

  .header-tabs {
    gap: var(--spacing-md);
  }

  .header-tab {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 13px;
    background: var(--bg-secondary);
    padding: 2px 8px;
    border-radius: 4px;
  }

  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: flex;
  }

  /* Mobile Filters - single row */
  .filters-bar {
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 8px;
    margin-bottom: 10px;
  }

  .search-container {
    flex: 1;
    min-width: 0;
  }

  .search-input {
    height: 44px;
    padding: 10px 14px;
    padding-right: 40px;
    font-size: 16px; /* Prevents iOS zoom */
    border-radius: 10px;
  }

  .search-icon {
    right: 12px;
    width: 18px;
    height: 18px;
  }

  .city-filter {
    width: 120px;
    flex-shrink: 0;
  }

  .city-select {
    height: 44px;
    padding: 10px 12px;
    font-size: 14px;
    border-radius: 10px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: left 10px center;
    padding-left: 30px;
  }

  .btn-clear {
    height: 44px;
    width: 44px;
    padding: 0;
    font-size: 0;
    border-radius: 10px;
    justify-content: center;
    flex-shrink: 0;
  }

  .btn-clear svg {
    width: 18px;
    height: 18px;
  }

  /* Mobile Results & Pagination - single compact row */
  .results-bar {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 8px;
    margin-bottom: 12px;
  }

  .results-info {
    padding-bottom: 0;
    border-bottom: none;
  }

  .results-count {
    font-size: 12px;
  }

  .pagination-controls {
    gap: 0;
  }

  .page-size-selector {
    display: none;
  }

  .page-nav {
    gap: 4px;
  }

  .page-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
  }

  .page-btn svg {
    width: 16px;
    height: 16px;
  }

  .page-indicator {
    font-size: 12px;
    min-width: 46px;
    font-weight: 500;
  }

  /* Remove card right border accent */
  .client-card {
    border-right: 1px solid var(--border-color);
  }

  .client-card:active {
    border-right-color: var(--border-color);
  }

  /* Mobile Loading Skeleton */
  .skeleton-card {
    border-radius: 12px;
    padding: 16px;
    min-height: 120px;
  }

  /* Mobile Empty/Error States */
  .error-state,
  .empty-state {
    padding: var(--spacing-xl);
  }

  .error-state svg,
  .empty-state svg {
    width: 56px;
    height: 56px;
  }

  .empty-text,
  .error-text {
    font-size: 15px;
  }
}

/* ============================================
   Scrollbar (horizontal only for table)
   ============================================ */
.table-container::-webkit-scrollbar {
  height: 6px;
}

.table-container::-webkit-scrollbar-track {
  background: var(--bg-secondary);
}

.table-container::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}
</style>
